import { Capacitor } from "@capacitor/core";
import {
  AdMob,
  RewardAdPluginEvents,
  InterstitialAdPluginEvents,
  type RewardAdOptions,
} from "@capacitor-community/admob";

const AD_UNIT_ID = "ca-app-pub-8432075357897846/8440194389";
const PREFLOP_INTERSTITIAL_AD_UNIT_ID = "ca-app-pub-8432075357897846/7485486461";

let initialized = false;

/** Timeout (ms) to prevent ad promises from hanging forever. */
const AD_TIMEOUT_MS = 30_000;

/** Show an interstitial at most once every INTERSTITIAL_INTERVAL rounds. */
const INTERSTITIAL_INTERVAL = 3;
let interstitialCounter = 0;

export async function initAdMob(): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  await AdMob.initialize({ initializeForTesting: false });
  initialized = true;
}

/**
 * Forward the user's consent choice to AdMob on native platforms.
 * Call this after the user interacts with the consent banner.
 */
export async function updateAdMobConsent(granted: boolean): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await initAdMob();
  try {
    if (granted) {
      await AdMob.requestTrackingAuthorization();
    }
  } catch {
    // not available on all platforms — safe to ignore
  }
}

/**
 * Load and show a rewarded ad.
 * Returns true if the user earned the reward, false otherwise.
 */
export async function showRewardedAd(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  await initAdMob();

  const options: RewardAdOptions = { adId: AD_UNIT_ID };
  await AdMob.prepareRewardVideoAd(options);

  return new Promise((resolve) => {
    let rewarded = false;
    let settled = false;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      rewardedHandle.then((h) => h.remove());
      dismissedHandle.then((h) => h.remove());
      failedHandle.then((h) => h.remove());
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, AD_TIMEOUT_MS);

    const rewardedHandle = AdMob.addListener(
      RewardAdPluginEvents.Rewarded,
      () => {
        rewarded = true;
      }
    );

    const dismissedHandle = AdMob.addListener(
      RewardAdPluginEvents.Dismissed,
      () => {
        cleanup();
        resolve(rewarded);
      }
    );

    const failedHandle = AdMob.addListener(
      RewardAdPluginEvents.FailedToShow,
      () => {
        cleanup();
        resolve(false);
      }
    );

    AdMob.showRewardVideoAd();
  });
}

/**
 * Load and show an interstitial ad for the Preflop Minigame.
 * Respects a frequency cap: shows at most once every INTERSTITIAL_INTERVAL rounds
 * to comply with AdMob policies against excessive ad frequency.
 * Resolves when the ad is dismissed or fails. No-op on web.
 */
export async function showPreflopInterstitialAd(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  interstitialCounter++;
  if (interstitialCounter % INTERSTITIAL_INTERVAL !== 0) return;

  await initAdMob();

  try {
    await AdMob.prepareInterstitial({ adId: PREFLOP_INTERSTITIAL_AD_UNIT_ID });

    await new Promise<void>((resolve) => {
      let settled = false;

      const cleanup = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        dismissedHandle.then((h) => h.remove());
        failedHandle.then((h) => h.remove());
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve();
      }, AD_TIMEOUT_MS);

      const dismissedHandle = AdMob.addListener(
        InterstitialAdPluginEvents.Dismissed,
        () => {
          cleanup();
          resolve();
        }
      );

      const failedHandle = AdMob.addListener(
        InterstitialAdPluginEvents.FailedToShow,
        () => {
          cleanup();
          resolve();
        }
      );

      AdMob.showInterstitial();
    });
  } catch {
    // ignore ad errors — never block game flow
  }
}

/** Reset the interstitial counter (call when starting a new session). */
export function resetInterstitialCounter(): void {
  interstitialCounter = 0;
}
